import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

interface MethodStep {
  name: string;
  description: string;
}

const methodSteps: MethodStep[] = [
  { name: 'Claridad', description: 'Entender qué está pasando realmente, sin ruido ni prisa.' },
  { name: 'Autoconocimiento', description: 'Identificar miedos, creencias, patrones y fortalezas que influyen en las decisiones.' },
  { name: 'Lectura del contexto', description: 'Analizar el entorno, el momento y las implicaciones profesionales y personales con realismo.' },
  { name: 'Movimiento consciente', description: 'Convertir decisiones en acciones concretas y sostenibles.' },
  { name: 'Alineación personal', description: 'Asegurar que la decisión sea congruente con valores, energía y etapa de vida.' },
];

export default function ComoLoHagoPage() {
  return (
    <>
      <Navbar />
      <main className="bg-background-50">
        {/* Header */}
        <section className="w-full pt-24 pb-8 md:pt-32 md:pb-12">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-[0.02em] text-foreground-900 leading-[1.05]">
              ¿CÓMO LO HAGO?
            </h1>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="w-full h-px bg-background-300/60" />
        </div>

        {/* Contenido principal */}
        <section className="w-full py-12 md:py-16">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
            {/* Intro + texto */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-28">
              <div className="lg:col-span-7 space-y-6">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  Normalmente, las personas buscan mentoría estratégica en momentos de inflexión organizacional, después de alguna 'disrupción' en su rutina diaria: un nuevo rol, retroalimentación distinta, nuevas metas o la conciencia de que algo ya no les resulta satisfactorio. Nuestro trabajo juntos es una oportunidad para explorar cómo transitar y aprovechar al máximo esos momentos de cambio.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  Trabajamos a través del método CALMA©, un enfoque propio desarrollado por Erika López Castañeda, basado en la escucha profunda, las preguntas correctas y la experiencia real.
                </p>
              </div>
              <div className="lg:col-span-5 flex items-end">
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70 w-full">
                  <p className="text-foreground-400 text-xs tracking-[0.2em] mb-3">EL PROCESO</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Cada sesión está diseñada para crear espacio de reflexión, claridad y movimiento consciente. No hay fórmulas rígidas: el método se adapta a tu momento profesional y personal.
                  </p>
                </div>
              </div>
            </div>

            {/* Método CALMA — sección destacada */}
            <div className="relative">
              {/* Header del método */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
                <div>
                  <p className="text-foreground-400 text-xs tracking-[0.2em] mb-3">5 PASOS</p>
                  <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.05]">
                    Método CALMA<span className="text-accent-500">©</span>
                  </h2>
                </div>
              </div>

              {/* Timeline de pasos */}
              <div className="relative">
                {/* Línea conectora */}
                <div className="hidden md:block absolute left-[60px] lg:left-[72px] top-8 bottom-8 w-px bg-background-200"></div>

                <div className="space-y-0">
                  {methodSteps.map((step, i) => (
                    <div key={i} className="relative flex gap-6 md:gap-8 lg:gap-10 py-7 md:py-8 group">
                      {/* Número */}
                      <div className="relative flex-shrink-0 w-12 h-12 md:w-16 md:h-16 lg:w-[72px] lg:h-[72px] rounded-full bg-background-100 border-2 border-background-200 flex items-center justify-center group-hover:border-accent-400 transition-colors duration-500 z-10">
                        <span className="font-heading text-lg md:text-2xl lg:text-3xl font-bold text-accent-500">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 pt-1 md:pt-2 lg:pt-3">
                        <h4 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-2 md:mb-3 tracking-[0.01em]">
                          {step.name}
                        </h4>
                        <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-[640px]">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA - Agenda una sesión */}
            <div className="flex flex-col items-center text-center mt-20 md:mt-28">
              <Link
                to="/agenda"
                className="inline-flex items-center gap-3 px-8 md:px-10 py-3.5 md:py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm md:text-base font-medium tracking-[0.03em] whitespace-nowrap transition-colors duration-300 cursor-pointer"
              >
                Agenda una sesión
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}